import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Breadcrumbs,
  Link,
  TextField,
  OutlinedInput,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Language as LanguageIcon,
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Label as LabelIcon,
} from '@mui/icons-material';
import api from './api';
import { processImageFile, validateImageFile } from './utils/imageUtils';
import { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE } from './utils/languages';
import TagSelector from './components/TagSelector';
import countriesData from './constants/countries.json';

// Countries array from the JSON file
const COUNTRIES = Object.entries(countriesData).map(([code, name]) => ({
  code,
  name
}));

export default function FoodDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [food, setFood] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);
  const [savingChanges, setSavingChanges] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [foodTags, setFoodTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [tagEditMode, setTagEditMode] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState([]);
  
  // Default languages
  const defaultLanguages = AVAILABLE_LANGUAGES;
  
  // Fetch food details by ID
  useEffect(() => {
    if (id) {
      fetchFoodDetails(id);
      fetchFoodTags(id);
    }
  }, [id]);
  
  // Fetch tags for the food
  const fetchFoodTags = async (foodId) => {
    try {
      const response = await api.get(`/tags/food/${foodId}`);
      if (response.data && response.data.data) {
        setFoodTags(response.data.data);
        setSelectedTagIds(response.data.data.map(tag => tag.id));
      }
    } catch (error) {
      console.error('Error fetching food tags:', error);
    }
  };
  
  const fetchFoodDetails = async (foodId) => {
    setLoading(true);
    try {
      const response = await api.get(`/foods/food/${foodId}`);
      
      if (response.data && response.data.success) {
        const foodDetail = response.data.data;
        
        // Parse languageSpec if it's a string
        if (typeof foodDetail.languageSpec === 'string') {
          foodDetail.languageSpec = JSON.parse(foodDetail.languageSpec);
        }
        
        // Extract country codes from the food data
        const countryCodes = foodDetail.foodCountries?.map(fc => fc.country?.code) || 
                           foodDetail.countries?.map(c => c.code) || 
                           [];
        setSelectedCountries(countryCodes.filter(Boolean));
        
        setFood(foodDetail);
        setEditMode(false);
      } else {
        console.error('Food not found or error in response');
      }
    } catch (error) {
      console.error('Error fetching food details:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Enable edit mode
  const handleEditClick = () => {
    // Ensure countries are properly set when entering edit mode
    const countryCodes = food.foodCountries?.map(fc => fc.country?.code) || 
                       food.countries?.map(c => c.code) || 
                       [];
    setSelectedCountries(countryCodes.filter(Boolean));
    setEditMode(true);
  };
  
  // Save tags
  const handleSaveTags = async () => {
    setSavingChanges(true);
    try {
      await api.post(`/tags/food/${food.id}`, { tagIds: selectedTagIds });
      await fetchFoodTags(food.id);
      setTagEditMode(false);
    } catch (error) {
      console.error('Error saving tags:', error);
    } finally {
      setSavingChanges(false);
    }
  };
  
  // Cancel edit mode
  const handleCancelEdit = () => {
    setEditMode(false);
    // Reset countries to original values
    const countryCodes = food.foodCountries?.map(fc => fc.country?.code) || 
                       food.countries?.map(c => c.code) || 
                       [];
    setSelectedCountries(countryCodes.filter(Boolean));
    // Reset any changes by re-fetching the food
    fetchFoodDetails(id);
  };
  
  // Navigate back to food search
  const handleBackToSearch = () => {
    navigate('/');
  };
  
  // Save language-specific name changes and countries
  const handleSaveLanguageName = async () => {
    setSavingChanges(true);
    try {
      // Get the current language data
      const languageSpec = food.languageSpec || {};
      
      // Use the main /foods/:id endpoint which accepts countries
      const response = await api.put(`/foods/${food.id}`, {
        name: food.name,
        languageSpec: languageSpec,
        countries: selectedCountries
      });
      
      if (response.data && response.data.success) {
        // After successful update, fetch the complete food details with relationships
        await fetchFoodDetails(food.id);
        setEditMode(false);
      } else {
        console.error('Failed to update food item');
      }
    } catch (error) {
      console.error('Error saving changes:', error);
    } finally {
      setSavingChanges(false);
    }
  };
  
  // Handle language name input change
  const handleLanguageNameChange = (value) => {
    const updatedLanguageSpec = { ...food.languageSpec } || {};
    
    if (!updatedLanguageSpec[currentLanguage]) {
      updatedLanguageSpec[currentLanguage] = {};
    }
    
    updatedLanguageSpec[currentLanguage].name = value;
    
    setFood({
      ...food,
      languageSpec: updatedLanguageSpec
    });
  };
  
  // Handle language image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      // Validate and process image
      validateImageFile(file);
      const processedFile = await processImageFile(file);
      
      const formData = new FormData();
      formData.append('image', processedFile);
      formData.append('language', currentLanguage);
      
      const response = await api.put(`/upload-image/upload-food-image/${food.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data && response.data.success) {
        // Parse languageSpec if it's a string
        const updatedFood = response.data.data;
        if (typeof updatedFood.languageSpec === 'string') {
          updatedFood.languageSpec = JSON.parse(updatedFood.languageSpec);
        }
        
        setFood(updatedFood);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setUploadingImage(false);
    }
  };
  
  // Handle language change
  const handleLanguageChange = (event) => {
    setCurrentLanguage(event.target.value);
  };
  
  // Get the display name for the current food based on selected language
  const getFoodDisplayName = () => {
    if (!food) return '';
    
    const languageSpec = food.languageSpec || {};
    
    // Use the language-specific name if available, otherwise fall back to default name
    return (languageSpec[currentLanguage]?.name) || food.name;
  };
  
  // Get available languages for the food
  const getAvailableLanguages = () => {
    if (!food || !food.languageSpec) return [];
    
    return Object.keys(food.languageSpec).filter(lang => 
      food.languageSpec[lang]?.name || food.languageSpec[lang]?.image
    );
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!food) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToSearch}
          sx={{ mb: 2 }}
        >
          Back to Search
        </Button>
        <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h5" color="text.secondary">
            Food not found
          </Typography>
        </Paper>
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link 
          component="button"
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Home
        </Link>
        <Link
          component="button"
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center' }}
          onClick={handleBackToSearch}
        >
          Food Search
        </Link>
        <Typography color="text.primary">{food.name}</Typography>
      </Breadcrumbs>
      
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBackToSearch}
        sx={{ mb: 3 }}
        variant="outlined"
      >
        Back to Search
      </Button>
      
      {/* Food Details Section */}
      <Paper elevation={3} sx={{ p: 3 }}>
        {!editMode ? (
          // View Mode
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
              <Typography variant="h4">{getFoodDisplayName()}</Typography>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEditClick}
              >
                Edit Food
              </Button>
            </Box>
            
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card elevation={2}>
                  <CardMedia
                    component="img"
                    height="300"
                    image={food?.image || "https://via.placeholder.com/400x320"}
                    alt={getFoodDisplayName()}
                    sx={{ objectFit: 'contain', bgcolor: 'background.default' }}
                  />
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Food Details</Typography>
                    <Typography variant="body1"><strong>ID:</strong> {food.id}</Typography>
                    <Typography variant="body1"><strong>Default Name:</strong> {food.name}</Typography>
                    <Typography variant="body1"><strong>Category:</strong> {food.category}</Typography>
                    <Typography variant="body1"><strong>Unit Type:</strong> {food.unitType ? food.unitType.replace('_', '/') : 'gram'}</Typography>
                    <Typography variant="body1"><strong>Unit Value:</strong> {food.unitValue || 100}</Typography>
                    {food.portionSize && (food.unitType === 'piece_portion' || food.unitType === 'piece' || food.unitType === 'portion') && (
                      <Typography variant="body1"><strong>Portion Size:</strong> {food.portionSize}g per piece</Typography>
                    )}
                    
                    {/* Countries Section */}
                    {(food.foodCountries?.length > 0 || food.countries?.length > 0) && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body1"><strong>Countries:</strong></Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                          {(food.foodCountries || food.countries || []).map((item, index) => {
                            const country = item.country || item;
                            return (
                              <Chip
                                key={country.id || country.code || index}
                                label={country.name || country.code}
                                size="small"
                                variant="outlined"
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Nutrition Information ({food.unitType === 'piece_portion' ? 'per piece' : `per ${food.unitValue || 100} ${food.unitType === 'ml' ? 'ml' : 'g'}`})
                    </Typography>
                    <Typography variant="body1"><strong>Calories:</strong> {food.calories}</Typography>
                    <Typography variant="body1"><strong>Protein:</strong> {food.protein}g</Typography>
                    <Typography variant="body1"><strong>Fat:</strong> {food.fat}g</Typography>
                    <Typography variant="body1"><strong>Carbs:</strong> {food.carbs}g</Typography>
                    <Typography variant="body1"><strong>Glycemic Index:</strong> {food.glycemicIndex}</Typography>
                    <Typography variant="body1"><strong>NOVA Group:</strong> {food.novaGroup ? `${food.novaGroup}${food.novaSource ? ` (${food.novaSource})` : ''}` : 'Not set'}</Typography>
                    
                    {/* Source URLs Section */}
                    {food.sourceUrls && food.sourceUrls.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="h6" gutterBottom>Validated Sources</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {food.sourceUrls.map((url, index) => (
                            <Link
                              key={index}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                textDecoration: 'underline',
                                color: 'primary.main',
                                fontSize: '0.875rem',
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                  borderRadius: '4px',
                                  padding: '4px 8px',
                                  margin: '-4px -8px'
                                }
                              }}
                            >
                              🔗 {url.replace(/^https?:\/\//, '').substring(0, 60)}
                              {url.replace(/^https?:\/\//, '').length > 60 && '...'}
                            </Link>
                          ))}
                        </Box>
                      </Box>
                    )}
                    
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" gutterBottom>Tags</Typography>
                      {!tagEditMode ? (
                        <Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            {foodTags.length > 0 ? (
                              foodTags.map((tag) => (
                                <Chip
                                  key={tag.id}
                                  label={`${tag.emoji} ${tag.name}`}
                                  size="small"
                                  icon={<LabelIcon />}
                                  sx={{
                                    backgroundColor: tag.color,
                                    color: tag.contrastColor || '#000',
                                  }}
                                />
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary">No tags assigned</Typography>
                            )}
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setTagEditMode(true)}
                          >
                            Edit Tags
                          </Button>
                        </Box>
                      ) : (
                        <Box>
                          <TagSelector
                            selectedTagIds={selectedTagIds}
                            onTagsChange={setSelectedTagIds}
                            label="Select tags"
                            placeholder="Choose tags..."
                          />
                          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={handleSaveTags}
                              disabled={savingChanges}
                            >
                              Save Tags
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setTagEditMode(false);
                                setSelectedTagIds(foodTags.map(tag => tag.id));
                              }}
                            >
                              Cancel
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>
                    
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" gutterBottom>Available Languages</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {getAvailableLanguages().map(lang => (
                          <Chip
                            key={lang}
                            label={lang.charAt(0).toUpperCase() + lang.slice(1)}
                            color={currentLanguage === lang ? "primary" : "default"}
                            variant={currentLanguage === lang ? "filled" : "outlined"}
                            icon={<LanguageIcon />}
                            onClick={() => setCurrentLanguage(lang)}
                          />
                        ))}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Language Versions</Typography>
                
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Select Language</InputLabel>
                  <Select
                    value={currentLanguage}
                    label="Select Language"
                    onChange={handleLanguageChange}
                  >
                    {defaultLanguages.map((lang) => (
                      <MenuItem key={lang} value={lang}>
                        {lang.charAt(0).toUpperCase() + lang.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <Card elevation={2} sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {currentLanguage.charAt(0).toUpperCase() + currentLanguage.slice(1)} Version
                    </Typography>
                    
                    <Typography variant="body1">
                      <strong>Name:</strong> {food.languageSpec?.[currentLanguage]?.name || 'Not set'}
                    </Typography>
                    
                    {food.languageSpec?.[currentLanguage]?.image ? (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body1" gutterBottom><strong>Image:</strong></Typography>
                        <Box sx={{ maxWidth: '100%', overflow: 'hidden', borderRadius: '4px' }}>
                          <img
                            src={food.languageSpec[currentLanguage].image}
                            alt={`${currentLanguage} food`}
                            style={{ width: '100%', maxHeight: '300px', objectFit: 'contain' }}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                        No image for this language
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        ) : (
          // Edit Mode
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
              <Typography variant="h4">Edit Food: {food.name}</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveLanguageName}
                  disabled={savingChanges}
                >
                  {savingChanges ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Box>
            
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Language Settings</Typography>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Language</InputLabel>
                  <Select
                    value={currentLanguage}
                    label="Language"
                    onChange={handleLanguageChange}
                  >
                    {defaultLanguages.map((lang) => (
                      <MenuItem key={lang} value={lang}>
                        {lang.charAt(0).toUpperCase() + lang.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <TextField
                  fullWidth
                  label={`Name (${currentLanguage})`}
                  value={(food.languageSpec?.[currentLanguage]?.name) || ''}
                  onChange={(e) => handleLanguageNameChange(e.target.value)}
                  sx={{ mb: 3 }}
                />
                
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{ height: '56px', mb: 3 }}
                  disabled={uploadingImage}
                >
                  {uploadingImage ? 'Uploading...' : `Upload ${currentLanguage.charAt(0).toUpperCase() + currentLanguage.slice(1)} Image`}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>
                
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Country Selection</Typography>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Select Countries</InputLabel>
                  <Select
                    multiple
                    value={selectedCountries}
                    onChange={(e) => setSelectedCountries(e.target.value)}
                    input={<OutlinedInput label="Select Countries" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((countryCode) => {
                          const country = COUNTRIES.find(c => c.code === countryCode);
                          return (
                            <Chip
                              key={countryCode}
                              label={country?.name || countryCode}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {COUNTRIES.map(country => (
                      <MenuItem key={country.code} value={country.code}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <img
                            src={`/images/country/${country.code.toLowerCase()}.svg`}
                            alt={country.name}
                            style={{ width: 20, height: 15 }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          {country.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Current {currentLanguage.charAt(0).toUpperCase() + currentLanguage.slice(1)} Preview</Typography>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="body1" gutterBottom>
                      <strong>Name:</strong> {food.languageSpec?.[currentLanguage]?.name || 'Not set'}
                    </Typography>
                    
                    {food.languageSpec?.[currentLanguage]?.image ? (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body1" gutterBottom><strong>Current Image:</strong></Typography>
                        <Box sx={{ maxWidth: '100%', overflow: 'hidden', borderRadius: '4px' }}>
                          <img
                            src={food.languageSpec[currentLanguage].image}
                            alt={`${currentLanguage} food preview`}
                            style={{ width: '100%', maxHeight: '300px', objectFit: 'contain' }}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ mt: 2, p: 3, bgcolor: 'background.default', borderRadius: '4px', textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          No image uploaded for this language
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>
    </Box>
  );
}